/** Where the applied-filter chips go (TZ §6): inline after the pills /
 *  action buttons while the toolbar's first row has room, otherwise the
 *  whole group moves to a second row under it. Decided from measured
 *  widths (row-1 children vs the chip group), never from overflow state,
 *  so it can't oscillate. */

import { useLayoutEffect, useState, type RefObject } from "react";

export function useChipsPlacement(
  toolbarRef: RefObject<HTMLElement | null>,
  chipsRef: RefObject<HTMLElement | null>,
  active: boolean,
): boolean {
  const [wrapped, setWrapped] = useState(false);
  useLayoutEffect(() => {
    const tb = toolbarRef.current;
    if (!tb || !active) { setWrapped(false); return; }
    const measure = () => {
      const chips = chipsRef.current;
      if (!chips) { setWrapped(false); return; }
      const cs = getComputedStyle(tb);
      const gap = parseFloat(cs.columnGap || cs.gap || "0") || 0;
      const inner = tb.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      let used = 0;
      let n = 0;
      for (const child of Array.from(tb.children)) {
        if (child === chips) continue;
        used += (child as HTMLElement).getBoundingClientRect().width;
        n += 1;
      }
      const available = inner - used - gap * n;
      setWrapped(chips.getBoundingClientRect().width > available);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(tb);
    if (chipsRef.current) ro.observe(chipsRef.current);
    return () => ro.disconnect();
  }, [toolbarRef, chipsRef, active]);
  return wrapped;
}
